/**
 * Code input utilities for 2FA verification
 */

export type CodeArray = string[];

/**
 * Handles single digit input for verification code
 * @param currentCode - Current code array
 * @param index - Index of the input field
 * @param value - New value entered
 * @returns Object with updated code and next focus index
 */
export function handleSingleDigitInput(
	currentCode: CodeArray,
	index: number,
	value: string,
): { newCode: CodeArray; shouldFocus?: number; shouldVerify?: boolean } {
	// Only allow digits
	if (!/^\d*$/.test(value)) {
		return { newCode: currentCode };
	}

	const newCode = [...currentCode];
	newCode[index] = value;

	// Handle single character input
	if (value.length === 1) {
		if (index < 5) {
			return { newCode, shouldFocus: index + 1 };
		} else {
			// Last input - check if code is complete
			const fullCode = newCode.join("");
			return { newCode, shouldVerify: fullCode.length === 6 };
		}
	}

	// Handle empty input (deletion)
	return { newCode };
}

/**
 * Handles multi-digit paste input
 * @param currentCode - Current code array
 * @param startIndex - Starting index for paste
 * @param pastedValue - Pasted value
 * @returns Object with updated code and actions
 */
export function handlePasteInput(
	currentCode: CodeArray,
	startIndex: number,
	pastedValue: string,
): { newCode: CodeArray; shouldFocus?: number; shouldVerify?: boolean } {
	const digits = pastedValue.replace(/\D/g, "").slice(0, 6);

	if (digits.length === 0) {
		return { newCode: currentCode };
	}

	const newCode = [...currentCode];
	const digitsArray = digits.split("");

	// Fill the code array starting from the paste position
	digitsArray.forEach((digit, i) => {
		if (startIndex + i < 6) {
			newCode[startIndex + i] = digit;
		}
	});

	// Determine next action
	if (digitsArray.length === 6) {
		return { newCode, shouldFocus: 5, shouldVerify: true };
	} else {
		const nextFocus = Math.min(startIndex + digitsArray.length, 5);
		return { newCode, shouldFocus: nextFocus };
	}
}

/**
 * Handles multi-character input (when user types/pastes multiple digits at once)
 * @param currentCode - Current code array
 * @param index - Starting index
 * @param value - Multi-character value
 * @returns Object with updated code and actions
 */
export function handleMultiCharacterInput(
	currentCode: CodeArray,
	index: number,
	value: string,
): { newCode: CodeArray; shouldFocus?: number; shouldVerify?: boolean } {
	const digits = value.slice(0, 6).split("");
	const newCode = [...currentCode];

	digits.forEach((digit, i) => {
		if (index + i < 6) {
			newCode[index + i] = digit;
		}
	});

	// Find next empty index or go to last input
	const nextEmptyIndex = newCode.findIndex((d) => !d);
	if (nextEmptyIndex !== -1) {
		return { newCode, shouldFocus: nextEmptyIndex };
	} else {
		const fullCode = newCode.join("");
		return {
			newCode,
			shouldFocus: 5,
			shouldVerify: fullCode.length === 6,
		};
	}
}

/**
 * Handles backspace key navigation
 * @param currentCode - Current code array
 * @param index - Current input index
 * @returns Index to focus or undefined
 */
export function handleBackspaceNavigation(
	currentCode: CodeArray,
	index: number,
): number | undefined {
	// If current field is empty and not the first field, go to previous
	if (!currentCode[index] && index > 0) {
		return index - 1;
	}
	return undefined;
}

/**
 * Clears the entire code array
 * @returns Empty code array
 */
export function clearCode(): CodeArray {
	return ["", "", "", "", "", ""];
}

/**
 * Checks if code is complete (all 6 digits filled)
 * @param code - Code array to check
 * @returns Boolean indicating if code is complete
 */
export function isCodeComplete(code: CodeArray): boolean {
	return code.length === 6 && code.every((digit) => digit !== "");
}

/**
 * Converts code array to string
 * @param code - Code array
 * @returns Joined code string
 */
export function codeToString(code: CodeArray): string {
	return code.join("");
}
